import { motion } from 'framer-motion';

export function LocationDetail() {
  const hours = [
    { day: 'Monday', time: '8:00 AM – 6:00 PM', open: true },
    { day: 'Tuesday', time: '8:00 AM – 6:00 PM', open: true },
    { day: 'Wednesday', time: '8:00 AM – 6:00 PM', open: true },
    { day: 'Thursday', time: '8:00 AM – 6:00 PM', open: true },
    { day: 'Friday', time: '8:00 AM – 6:00 PM', open: true },
    { day: 'Saturday', time: 'Closed', open: false },
    { day: 'Sunday', time: 'Closed', open: false },
  ];

  const contactDetails = [
    {
      icon: 'ri-map-pin-2-line',
      label: 'Address',
      value: '2400 W Pioneer Pkwy #118\nArlington, Texas 76013',
      href: 'https://www.google.com/maps/search/?api=1&query=32.708617,-97.147128&query_place_id=ChIJkRKWLsV8ToYRhAcULCMpvSc',
      color: 'from-teal-500 to-cyan-500',
    },
    {
      icon: 'ri-phone-line',
      label: 'Phone',
      value: '817-860-9310',
      href: 'tel:817-860-9310',
      color: 'from-orange-500 to-red-400',
    },
    {
      icon: 'ri-facebook-fill',
      label: 'Facebook',
      value: '@SignaramaArlingtonTX',
      href: 'https://www.facebook.com/SignaramaArlingtonTX/',
      color: 'from-emerald-500 to-teal-500',
    },
    {
      icon: 'ri-google-fill',
      label: 'Google Business',
      value: 'Leave a Review',
      href: 'https://g.co/kgs/1jxvQT5',
      color: 'from-amber-500 to-orange-500',
    },
  ];

  return (
    <section id="location-detail" className="py-24 bg-white">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <span className="text-teal-600 text-sm font-semibold tracking-widest uppercase mb-3 block">
            Visit Us
          </span>
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
            Signarama Arlington{' '}
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-teal-600 to-orange-500">
              Location
            </span>
          </h2>
          <p className="text-lg text-gray-600 max-w-3xl mx-auto">
            Conveniently located in Arlington, TX — serving the entire DFW Metroplex with
            professional custom signage solutions.
          </p>
        </motion.div>

        {/* Large Map */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="rounded-3xl overflow-hidden border border-gray-200 mb-12 w-full h-96"
        >
          <iframe
            title="Signarama Arlington Location"
            src="https://maps.google.com/maps?q=2400+W+Pioneer+Pkwy+%23118+Arlington+TX+76013&output=embed&z=15"
            width="100%"
            height="100%"
            style={{ border: 0 }}
            allowFullScreen
            loading="lazy"
            referrerPolicy="no-referrer-when-downgrade"
          ></iframe>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
          {/* Contact Details */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <h3 className="text-2xl font-bold text-gray-900 mb-8">Contact &amp; Find Us</h3>
            <div className="space-y-4">
              {contactDetails.map((detail, index) => (
                <a
                  key={index}
                  href={detail.href}
                  target={detail.href.startsWith('http') ? '_blank' : undefined}
                  rel={detail.href.startsWith('http') ? 'noopener noreferrer' : undefined}
                  className="flex items-center space-x-4 p-4 bg-gray-50 rounded-xl hover:bg-gray-100 transition-colors cursor-pointer"
                >
                  <div
                    className={`w-12 h-12 bg-gradient-to-br ${detail.color} rounded-xl flex items-center justify-center flex-shrink-0`}
                  >
                    <i className={`${detail.icon} text-xl text-white`}></i>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500 font-medium mb-0.5">{detail.label}</p>
                    <p className="text-gray-900 text-sm font-semibold whitespace-pre-line">
                      {detail.value}
                    </p>
                  </div>
                </a>
              ))}
            </div>

            <div className="mt-8 p-6 bg-gradient-to-br from-teal-50 to-cyan-50 border border-teal-100 rounded-2xl">
              <div className="flex items-start space-x-3">
                <div className="w-8 h-8 bg-teal-500 rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5">
                  <i className="ri-information-line text-white text-sm"></i>
                </div>
                <div>
                  <h4 className="font-bold text-gray-900 text-sm mb-1">Walk-Ins Welcome</h4>
                  <p className="text-gray-600 text-xs leading-relaxed">
                    No appointment needed during business hours. Our team is always ready to discuss
                    your signage project, view samples, and provide expert recommendations at no
                    charge.
                  </p>
                </div>
              </div>
            </div>
          </motion.div>

          {/* Hours */}
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <h3 className="text-2xl font-bold text-gray-900 mb-8">Business Hours</h3>
            <div className="space-y-2">
              {hours.map((hour, index) => (
                <div
                  key={index}
                  className={`flex items-center justify-between py-3 px-5 rounded-xl ${
                    hour.open ? 'bg-gray-50' : 'bg-gray-50 opacity-50'
                  }`}
                >
                  <span className="text-gray-700 text-sm font-medium">{hour.day}</span>
                  <span
                    className={`text-sm font-semibold ${
                      hour.open ? 'text-teal-600' : 'text-gray-400'
                    }`}
                  >
                    {hour.time}
                  </span>
                </div>
              ))}
            </div>

            <div className="mt-8 p-6 bg-gray-900 rounded-2xl">
              <h4 className="text-white font-bold mb-3">After-Hours Inquiries</h4>
              <p className="text-gray-400 text-sm leading-relaxed mb-4">
                Need a quote outside business hours? Submit your request online and we&apos;ll
                respond the next business day.
              </p>
              <button
                onClick={() => {
                  const el = document.getElementById('get-quote');
                  if (el) el.scrollIntoView({ behavior: 'smooth' });
                }}
                className="w-full py-3 bg-gradient-to-r from-teal-500 to-teal-600 text-white text-sm font-semibold rounded-xl hover:from-teal-600 hover:to-teal-700 transition-all whitespace-nowrap cursor-pointer"
              >
                Request a Quote Online
              </button>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
